import { z } from "zod";

/**
 * Phase 6a: the persisted-export wire-format version. Every `ExportEnvelope`
 * carries this number; Phase 6c's migration runner reads it and walks the
 * envelope forward to `SCHEMA_VERSION` when the stored value is older.
 *
 * Starts at 1. Increment only when a field's wire shape changes in a way
 * a migration step has to handle — never for additive optional fields,
 * which decode transparently against the older shape.
 */
export const SCHEMA_VERSION = 1 as const;

/**
 * Discriminant for the envelope payload. The codec is the canonical
 * shape consumed by goldens, Phase 9 sync, and Phase 6c migration; each
 * payload kind is a separate decode entry point so callers can declare
 * what they expect at the type level (no `unknown` after a successful
 * decode).
 *
 * Phase 6a covers `site-graph`, `panel`, and `warning-set`. Phase 7a
 * adds `legend-document`. Phase 9a adds `operation-log` for the
 * offline-first sync wire shape. Phase 8's `suggestion-set` lands
 * when that phase chooses to persist suggestion sets.
 */
export type ExportPayloadKind =
  | "site-graph"
  | "panel"
  | "warning-set"
  | "legend-document"
  | "operation-log"
  | "share-link";

export type ExportPayload<Data> = {
  readonly kind: ExportPayloadKind;
  readonly data: Data;
};

/**
 * `ExportEnvelope` per Phase 6a — the single canonical persistence
 * shape. `payload` is a `{ kind, data }` pair so a reader can branch on
 * the discriminant before validating the body. `exportedAt` is an
 * ISO 8601 instant in canonical millisecond-precision UTC.
 */
export type ExportEnvelope<Data = unknown> = {
  readonly schemaVersion: number;
  readonly exportedAt: string;
  readonly payload: ExportPayload<Data>;
};

/**
 * ISO 8601 UTC instant with millisecond precision and a `Z` suffix per
 * the Phase 6a determinism rule. Validated on encode (so a malformed
 * domain value fails fast) and on decode (so a corrupted envelope is
 * rejected before it reaches a smart factory).
 *
 * Pattern: `YYYY-MM-DDTHH:MM:SS.sssZ`. The codec does not accept
 * timezone offsets, sub-millisecond precision, or trailing whitespace —
 * Phase 9 sync relies on lexicographic ordering matching chronological
 * ordering, which only holds for this canonical form.
 */
const INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

export const instantSchema = z
  .string()
  .regex(
    INSTANT_PATTERN,
    "Instant must be ISO 8601 UTC with millisecond precision (YYYY-MM-DDTHH:MM:SS.sssZ).",
  );

export function isCanonicalInstant(s: string): boolean {
  return INSTANT_PATTERN.test(s);
}

/**
 * Normalize a value to a canonical instant string for use in golden
 * fixtures and round-trip tests where the test owns the clock. Throws
 * synchronously on inputs the JavaScript date constructor refuses.
 *
 * Production code stamps instants at the API boundary (Phase 12); this
 * helper exists only for tests and for the `serializeSiteGraph` /
 * `serializePanel` callers that haven't yet been wired to the boundary
 * clock.
 */
export function canonicalInstant(value: string | number | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  const ms = d.getTime();
  if (Number.isNaN(ms)) {
    throw new CodecError(
      "INVALID_INSTANT",
      `canonicalInstant: value "${String(value)}" is not a parseable date.`,
    );
  }
  return d.toISOString();
}

export type CodecErrorCode =
  | "INVALID_PAYLOAD"
  | "INVALID_INSTANT"
  | "UNSUPPORTED_SCHEMA_VERSION";

/**
 * Base error thrown from the codec when a decode rejects an envelope or
 * an encode rejects a malformed domain value. Carries a typed `code`
 * for callers that want to branch on the failure mode without parsing
 * the message.
 */
export class CodecError extends Error {
  override readonly name: string = "CodecError";
  readonly code: CodecErrorCode;
  constructor(code: CodecErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

/**
 * Thrown when `deserialize*` is handed an envelope whose `schemaVersion`
 * is greater than the codec's `SCHEMA_VERSION`. We never downgrade —
 * field-shape regressions are out of scope.
 *
 * Phase 6c's migration runner handles the `observed < SCHEMA_VERSION`
 * case transparently by walking the envelope forward through registered
 * migration steps; a missing step in that chain throws
 * `MissingMigrationStepError` from `migrations.ts`, not this error.
 *
 * Carries both the observed and supported versions so callers can
 * surface a precise diagnostic.
 */
export class UnsupportedSchemaVersionError extends CodecError {
  override readonly name: string = "UnsupportedSchemaVersionError";
  readonly observed: number;
  readonly supported: number;
  constructor(observed: number, supported: number) {
    super(
      "UNSUPPORTED_SCHEMA_VERSION",
      `Envelope schemaVersion ${observed} is not supported by this codec ` +
        `(SCHEMA_VERSION=${supported}). Phase 6c will upgrade older versions ` +
        "via the migration runner; newer versions are rejected — the codec " +
        "does not downgrade.",
    );
    this.observed = observed;
    this.supported = supported;
  }
}

/**
 * Build an envelope around a payload. Stamps `schemaVersion` to the
 * current codec version and validates `exportedAt` is canonical.
 *
 * Callers should pass a canonical instant (`canonicalInstant(...)` is
 * the helper); a non-canonical string fails with `INVALID_INSTANT`.
 */
export function envelope<Data>(
  payload: ExportPayload<Data>,
  exportedAt: string,
): ExportEnvelope<Data> {
  if (!isCanonicalInstant(exportedAt)) {
    throw new CodecError(
      "INVALID_INSTANT",
      `envelope: exportedAt "${exportedAt}" is not a canonical ISO 8601 UTC ` +
        "instant with millisecond precision. Use `canonicalInstant(...)` to " +
        "normalize before passing in.",
    );
  }
  return {
    schemaVersion: SCHEMA_VERSION,
    exportedAt,
    payload,
  };
}

/**
 * Reject envelopes whose `schemaVersion` is not exactly the codec's
 * current version. Retained for callers that want to assert
 * "this envelope must already be at the current version" without
 * running the migration chain. The Phase 6a / 6b entry points used this
 * directly; Phase 6c's `serialize.ts` now routes through
 * `runMigrations` so older envelopes upgrade transparently and only
 * `observed > SCHEMA_VERSION` reaches `UnsupportedSchemaVersionError`.
 */
export function assertSupportedSchemaVersion(observed: number): void {
  if (observed !== SCHEMA_VERSION) {
    throw new UnsupportedSchemaVersionError(observed, SCHEMA_VERSION);
  }
}

/**
 * Top-level envelope frame validator. Pulls `schemaVersion`,
 * `exportedAt`, and the payload kind out of an unknown JSON value so a
 * caller can route to the right `decode*` entry point.
 *
 * The payload body is intentionally `unknown` here — the per-kind
 * decoders own its shape.
 */
export const envelopeFrameSchema = z.object({
  schemaVersion: z.number().int().nonnegative(),
  exportedAt: instantSchema,
  payload: z.object({
    kind: z.enum([
      "site-graph",
      "panel",
      "warning-set",
      "legend-document",
      "operation-log",
      "share-link",
    ]),
    data: z.unknown(),
  }),
});

export type EnvelopeFrame = z.infer<typeof envelopeFrameSchema>;
